import { useState, useEffect, useRef } from 'react'
import { ChevronDown, PieChart, RefreshCw, Printer } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import leiloesService from '../../services/leiloesService'
import promotoresService from '../../services/promotoresService'

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

export default function DistribuicaoComissao() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    promotoresService.getDistribuicaoComissao(orgId, leilaoId)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId])

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await promotoresService.getDistribuicaoComissaoPdf(orgId, leilaoId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url); setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }

  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <PieChart className="w-5 h-5 text-indigo-600" />
        <h1 className="text-lg font-semibold text-gray-800">Distribuição de Comissão</h1>
        {loading && <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={setLeilaoId} disabled={loadingL} className="flex-[3] min-w-[250px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <button onClick={handleImprimir} disabled={!dados || loadingPdf}
            className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors self-end">
            <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Selecione um leilão</div>
      ) : (
        <div className="space-y-4">
          {/* Resumo Geral */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Resumo do Leilão</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-xs text-blue-600 font-medium">Vendas</div>
                <div className="text-lg font-bold text-blue-800">{brl(dados.totais.vendas_valor)}</div>
                <div className="text-xs text-blue-500">{dados.totais.vendas_lotes} lotes</div>
              </div>
              {dados.totais.defesas_valor > 0 && (
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xs text-orange-600 font-medium">Defesas</div>
                  <div className="text-lg font-bold text-orange-800">{brl(dados.totais.defesas_valor)}</div>
                  <div className="text-xs text-orange-500">{dados.totais.defesas_lotes} lotes</div>
                </div>
              )}
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-xs text-green-600 font-medium">Comissão Total</div>
                <div className="text-lg font-bold text-green-800">{brl(dados.totais.comissao_total)}</div>
              </div>
              <div className="text-center p-3 bg-indigo-50 rounded-lg">
                <div className="text-xs text-indigo-600 font-medium">Organizador (Líquido)</div>
                <div className="text-lg font-bold text-indigo-800">{brl(dados.organizador.liquido)}</div>
              </div>
            </div>
          </div>

          {/* Tabela de Distribuição */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Participante</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">% Venda</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">% Defesa</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Comissão Venda</th>
                  {dados.totais.defesas_valor > 0 && <th className="text-right px-4 py-3 font-medium text-gray-600">Comissão Defesa</th>}
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Organizador */}
                <tr className="border-b border-gray-100 bg-indigo-50/50">
                  <td className="px-4 py-3 font-semibold text-indigo-800">
                    🏢 Organizador (Líquido)
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500">—</td>
                  <td className="px-4 py-3 text-center text-gray-500">—</td>
                  <td className="px-4 py-3 text-right text-gray-500">—</td>
                  {dados.totais.defesas_valor > 0 && <td className="px-4 py-3 text-right text-gray-500">—</td>}
                  <td className="px-4 py-3 text-right font-bold text-indigo-800">{brl(dados.organizador.liquido)}</td>
                </tr>

                {/* Promotores */}
                {dados.promotores.map(p => (
                  <tr key={`p-${p.id}`} className="border-b border-gray-100 hover:bg-purple-50/30">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span className="text-purple-600">👤</span> {p.nome}
                      <span className="text-xs text-purple-500 ml-1.5">(Promotor)</span>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-purple-700">{p.perc_venda}%</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">{p.perc_defesa}%</td>
                    <td className="px-4 py-3 text-right">{brl(p.comissao_venda)}</td>
                    {dados.totais.defesas_valor > 0 && <td className="px-4 py-3 text-right">{brl(p.comissao_defesa)}</td>}
                    <td className="px-4 py-3 text-right font-bold">{brl(p.total)}</td>
                  </tr>
                ))}

                {/* Leiloeiros */}
                {dados.leiloeiros.map(l => (
                  <tr key={`l-${l.id}`} className="border-b border-gray-100 hover:bg-amber-50/30">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span className="text-amber-600">🔨</span> {l.nome}
                      <span className="text-xs text-amber-500 ml-1.5">(Leiloeiro)</span>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-amber-700">{l.perc_venda}%</td>
                    <td className="px-4 py-3 text-center font-semibold text-orange-600">{l.perc_defesa}%</td>
                    <td className="px-4 py-3 text-right">{brl(l.comissao_venda)}</td>
                    {dados.totais.defesas_valor > 0 && <td className="px-4 py-3 text-right">{brl(l.comissao_defesa)}</td>}
                    <td className="px-4 py-3 text-right font-bold">{brl(l.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-800">TOTAL</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-right font-bold">—</td>
                  {dados.totais.defesas_valor > 0 && <td className="px-4 py-3 text-right font-bold">—</td>}
                  <td className="px-4 py-3 text-right font-bold text-lg">{brl(dados.totais.comissao_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Detalhamento Organizador */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Detalhamento do Organizador</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Comissão Bruta (total arrecadado)</span><span className="font-semibold">{brl(dados.organizador.comissao_bruta)}</span></div>
              {dados.organizador.desconto_promotores > 0 && (
                <div className="flex justify-between"><span className="text-purple-600">(-) Promotores</span><span className="font-semibold text-purple-700">- {brl(dados.organizador.desconto_promotores)}</span></div>
              )}
              {dados.organizador.desconto_leiloeiros > 0 && (
                <div className="flex justify-between"><span className="text-amber-600">(-) Leiloeiros</span><span className="font-semibold text-amber-700">- {brl(dados.organizador.desconto_leiloeiros)}</span></div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200"><span className="font-bold text-indigo-800">Líquido Organizador</span><span className="font-bold text-indigo-800 text-lg">{brl(dados.organizador.liquido)}</span></div>
            </div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Distribuição de Comissão</span>
              <div className="flex items-center gap-2">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Distribuição de Comissão" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
